import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link2, Loader2 } from 'lucide-react';
import { inquiriesAPI } from '../../services/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';

/** Attach quotations that were created directly in the Quotations module. */
export const LinkQuotationDialog = ({ inquiryId, open, onOpenChange, onLinked }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await inquiriesAPI.linkableQuotations(inquiryId);
      setRows(response.data.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load quotations');
    } finally {
      setLoading(false);
    }
  }, [inquiryId]);

  useEffect(() => {
    if (open) { setSelected(new Set()); setSearch(''); load(); }
  }, [open, load]);

  const toggle = (id) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const link = async () => {
    setLinking(true);
    try {
      const response = await inquiriesAPI.linkQuotation(inquiryId, Array.from(selected));
      toast.success(response.data.message);
      if (response.data.skipped?.length) {
        toast.warning(response.data.skipped.map((row) => `${row.reference || row.id}: ${row.reason}`).join('; '));
      }
      onOpenChange(false);
      onLinked?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Linking failed');
    } finally {
      setLinking(false);
    }
  };

  const visible = rows.filter((row) =>
    !search || (row.quotation_number || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="link-quotation-dialog">
        <DialogHeader>
          <DialogTitle>Link an existing quotation</DialogTitle>
          <DialogDescription>
            Quotations raised for this customer that are not yet attached to an inquiry.
          </DialogDescription>
        </DialogHeader>

        <Input value={search} onChange={(event) => setSearch(event.target.value)}
          placeholder="Search quotation number" data-testid="link-quotation-search" />

        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : visible.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="link-quotation-empty">
              No unlinked quotations for this customer.
            </p>
          ) : visible.map((row) => (
            <label key={row.id} className="flex cursor-pointer items-center gap-3 border p-3"
              data-testid={`linkable-quotation-${row.id}`}>
              <Checkbox checked={selected.has(row.id) || row.already_linked} disabled={row.already_linked}
                onCheckedChange={() => toggle(row.id)}
                data-testid={`linkable-quotation-checkbox-${row.id}`} />
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold">{row.quotation_number}</span>
                  <Badge variant="outline">{row.status}</Badge>
                  {row.shares_parts && <Badge className="bg-blue-100 text-blue-800">same parts</Badge>}
                  {row.already_linked && <Badge className="bg-emerald-100 text-emerald-800">already linked</Badge>}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {row.item_count} item(s) · {row.currency} {Number(row.total_amount || 0).toLocaleString('en-IN')}
                  {row.created_at ? ` · ${new Date(row.created_at).toLocaleDateString('en-IN')}` : ''}
                </p>
              </div>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="link-quotation-cancel">Cancel</Button>
          <Button disabled={selected.size === 0 || linking} onClick={link} data-testid="link-quotation-confirm">
            {linking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
            Link {selected.size > 0 ? selected.size : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkQuotationDialog;
